import { Handle, Position } from '@xyflow/react';
import { TriangleAlert } from 'lucide-react';
import * as Icons from 'lucide-react';
import { DynamicIcon } from './DynamicIcon';
import { WorkflowCustomNodeCategory } from '@/types/agents';
import { cn } from '@/lib/utils';

interface CustomNodeProperty {
    name: string;
    type: string;
    required: boolean;
    description: string;
    defaultValue: unknown;
    value: unknown;
}

interface CustomNodeOptions {
    allowInput: boolean;
    allowOutput: boolean;
}

interface CustomNodeConnector {
    id: string;
    label: string;
    category: string;
    schema: string;
    required: boolean;
    allowMultiple: boolean;
}

interface CustomNodeProps {
    data: {
        id: string;
        label: string;
        description?: string;
        icon: string;
        category?: WorkflowCustomNodeCategory;
        options?: CustomNodeOptions;
        properties?: {
            [key: string]: CustomNodeProperty;
        },
        inputs?: CustomNodeConnector[];
        outputs?: CustomNodeConnector[];
    };
}

function getCategoryHeaderClass(category?: WorkflowCustomNodeCategory) {
    switch (category) {
        case 'Generators':
            return 'bg-primary text-primary-foreground';
        case 'Processors':
            return 'bg-secondary text-secondary-foreground';
        case 'Control Flow':
            return 'bg-destructive text-destructive-foreground';
        case 'Utility':
            return 'bg-muted text-muted-foreground';
        default:
            return 'bg-accent text-accent-foreground';
    }
}

function CustomNode({ data }: CustomNodeProps) {
    return (
        <div className="shadow-md rounded-md bg-white border-2 border-stone-400 relative">
            {/* Header with primary color */}
            <div className={`${getCategoryHeaderClass(data?.category)} px-4 py-2 flex items-center justify-between relative rounded-t-sm`}>
                <div className="flex items-center">
                    <div className="rounded-md w-8 h-8 flex justify-center items-center bg-white/20 mr-2">
                        <DynamicIcon name={data?.icon as keyof typeof Icons} size={12} className="text-white" />
                    </div>
                    <div className="font-bold">{data?.label}</div>
                </div>
            </div>

            <div className="absolute top-[-10px] right-[-10px]">
                <div className="relative group">
                    <TriangleAlert size={16} className="text-secondary cursor-help" />
                    <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                        Node configuration warning
                        <div className="absolute top-full right-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                    </div>
                </div>
            </div>

            {/* Body with description */}
            <div className="px-4 py-3">
                {data?.description && (
                    <div className="text-gray-600">{data.description}</div>
                )}
            </div>

            <Handle
                type="target"
                about="Input 1"
                id={`${data?.id}-input-1`}
                position={Position.Left}
                isConnectable={true}
                isValidConnection={(connection) => {
                    return connection.sourceHandle === 'output';
                }}
                className="w-24 bg-secondary p-1"
                style={{ top: '75%' }}
            />
            {/* <Handle
                type="target"
                about="Input 2"
                id={`${data?.id}-input-2`}
                position={Position.Left}
                isConnectable={true}
                isValidConnection={(connection) => {
                    return connection.sourceHandle === 'output';
                }}
                className="bg-secondary flex items-center justify-center w-10 h-10 p-1"
                style={{ top: '66%' }}
            >
            </Handle> */}
            {/* <div
                className="bg-gray-300 text-white text-xs rounded p-1"
                style={{
                    position: 'absolute',
                    left: '0',
                    top: '66%',
                    transform: 'translate(15%, -50%)',
                    fontSize: '0.8em',
                    color: '#555',
                    whiteSpace: 'nowrap',
                }}
            >
                Input 2
            </div> */}
            <Handle
                type="source"
                about="Output 1"
                id={`${data?.id}-output-1`}
                position={Position.Right}
                isConnectable={true}
                isValidConnection={(_) => {
                    return true;
                }}
                className="w-24 bg-secondary p-1"
                style={{ top: '75%' }}
            />

            {/* <Handle
                type="target"
                position={Position.Left}
                isConnectable={data?.options?.allowInput}
                isValidConnection={(connection) => {
                    return connection.sourceHandle === 'output';
                }}
                hidden={!data?.options?.allowInput}
                className="w-24 bg-secondary"
            />
            <Handle
                type="source"
                position={Position.Right}
                isConnectable={data?.options?.allowOutput}
                hidden={!data?.options?.allowOutput}
                className="w-24 bg-secondary"
            /> */}
        </div>
    );
}

export default CustomNode;